import {
  faFilter,
  faFilterCircleXmark,
  faRefresh,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { DateTime } from "luxon";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Scroll from "react-scroll";
import { useRecoilState } from "recoil";
import { getPromoCodeUsersListAPI } from "../../../API/PromoCode";
import { usersCreatedState, usersShowFilterState } from "../../../AppState";
import FormErrorBox from "../../../Components/Reusable/FormErrorBox";
import FormSelectField from "../../../Components/Reusable/FormSelectField";
import PageLoadingContent from "../../../Components/Reusable/PageLoadingContent";
import {
  PAGE_SIZE_OPTIONS,
  USER_CREATED_LIST_OPTIONS,
} from "../../../Constants/FieldOptions";

const ActiveUsers = ({ id }) => {
  const [errors, setErrors] = useState({});
  const [isFetching, setFetching] = useState(false);

  const [listData, setListData] = useState("");
  const [pageSize, setPageSize] = useState(10);
  const [previousCursors, setPreviousCursors] = useState([]);
  const [nextCursor, setNextCursor] = useState("");
  const [currentCursor, setCurrentCursor] = useState("");
  const [showFilter, setShowFilter] = useRecoilState(usersShowFilterState);
  const [created, setCreated] = useRecoilState(usersCreatedState);

  function onPromoCodeListSuccess(response) {
    setListData(response);
    if (response.hasNextPage) {
      setNextCursor(response.nextCursor);
    }
  }

  function onPromoCodeListError(apiErr) {
    setErrors(apiErr);

    var scroll = Scroll.animateScroll;
    scroll.scrollToTop();
  }

  function onPromoCodeListDone() {
    setFetching(false);
  }

  const fetchList = (cur, limit, cdr) => {
    setFetching(true);
    setErrors({});

    let params = new Map();

    params.set("page_size", limit);

    if (cur !== "") {
      params.set("cursor", cur);
    }

    params.set("promoCodeId", id);

    let end = Math.floor(Date.now() / 1000);

    let start = end - cdr * 24 * 60 * 60;

    params.set("created_gte", start);
    params.set("created_lte", end);

    getPromoCodeUsersListAPI(
      params,
      onPromoCodeListSuccess,
      onPromoCodeListError,
      onPromoCodeListDone
    );
  };

  const onNextClicked = (e) => {
    let arr = [...previousCursors];
    arr.push(currentCursor);
    setPreviousCursors(arr);
    setCurrentCursor(nextCursor);
  };

  const onPreviousClicked = (e) => {
    let arr = [...previousCursors];
    const previousCursor = arr.pop();
    setPreviousCursors(arr);
    setCurrentCursor(previousCursor);
  };

  const defaultFetchList = () => {
    fetchList(currentCursor, pageSize, created);
  };

  const onClearFilterClick = (e) => {
    setShowFilter(false);
    setCreated(368);
  };

  useEffect(() => {
    let mounted = true;

    if (mounted) {
      window.scrollTo(0, 0);
      fetchList(currentCursor, pageSize, created);
    }

    return () => {
      mounted = false;
    };
  }, [currentCursor, pageSize, created]);

  return (
    <div className="b-table">
      <div className="table-wrapper has-mobile-cards">
        <div className="is-hidden-touch">
          <div className="columns">
            {!isFetching && (
              <div className="column has-text-right">
                <button
                  className="button is-small is-info is-fullwidth-mobile"
                  onClick={defaultFetchList}
                >
                  <FontAwesomeIcon className="mdi" icon={faRefresh} />
                  <span className="is-hidden-desktop is-hidden-tablet">
                    &nbsp;Refresh
                  </span>
                </button>
                &nbsp;
                <button
                  onClick={(e, i) => {
                    setShowFilter(!showFilter);
                  }}
                  className="button is-small is-link is-fullwidth-mobile"
                  type="button"
                >
                  <FontAwesomeIcon className="mdi" icon={faFilter} />
                  &nbsp;Filter
                </button>
              </div>
            )}
          </div>
          {showFilter && (
            <>
              <div
                className="has-background-white-bis"
                style={{ borderRadius: "15px", padding: "20px" }}
              >
                {/* Filter Title + Clear Button */}
                <div className="columns">
                  <div className="column is-half">
                    <strong>
                      <u>
                        <FontAwesomeIcon className="mdi" icon={faFilter} />
                        &nbsp;Filter
                      </u>
                    </strong>
                  </div>
                  <div className="column is-half has-text-right">
                    <Link onClick={onClearFilterClick}>
                      <FontAwesomeIcon
                        className="mdi"
                        icon={faFilterCircleXmark}
                      />
                      &nbsp;Clear Filter
                    </Link>
                  </div>
                </div>

                {/* Filter Options */}
                <div className="columns">
                  <div className="column is-half">
                    <FormSelectField
                      label="Created"
                      name="created"
                      placeholder="Text input"
                      selectedValue={parseInt(created)}
                      options={USER_CREATED_LIST_OPTIONS}
                      errorText={errors && errors.created}
                      helpText=""
                      onChange={(e) => setCreated(parseInt(e.target.value))}
                      maxWidth="150px"
                    />
                  </div>
                </div>
              </div>
            </>
          )}
          <table className="table is-fullwidth is-striped is-hoverable is-fullwidth">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Joining date </th>
              </tr>
            </thead>
            <tbody>
              {isFetching ? (
                <tr>
                  <td colSpan={3}>
                    <PageLoadingContent displayMessage={"Please wait"} />
                  </td>
                </tr>
              ) : (
                <>
                  <FormErrorBox errors={errors} />
                  {listData &&
                    listData?.map((item, i) => (
                      <tr key={i}>
                        <td data-label="Name" className="capitalize">
                          {item.firstName || item.lastName
                            ? `${item.firstName} ${item.lastName}`
                            : "N/a"}
                        </td>
                        <td data-label="Name" className="capitalize">
                          {item?.email}
                        </td>
                        <td data-label="Name" className="capitalize">
                          {DateTime.fromSeconds(item.created).toLocaleString(
                            DateTime.DATETIME_MED
                          )}
                        </td>
                      </tr>
                    ))}
                </>
              )}
            </tbody>
          </table>
        </div>
        <div className="is-fullwidth is-hidden-desktop">
          {listData &&
            listData?.map(function (item, i) {
              return (
                <div class="mb-5" key={item.id}>
                  <hr />
                  <strong>Name:</strong>&nbsp;{" "}
                  {item.firstName || item.lastName
                    ? `${item.firstName} ${item.lastName}`
                    : "N/a"}
                  <br />
                  <br />
                  <strong>Email:</strong>&nbsp;{item?.email}
                  <br />
                  <br />
                  <strong>Joining date:</strong>&nbsp;
                  {DateTime.fromSeconds(item.created).toLocaleString(
                    DateTime.DATETIME_MED
                  )}
                </div>
              );
            })}
        </div>

        <div className="columns">
          <div className="column is-half">
            <span className="select">
              <select
                className={`input has-text-grey-light`}
                onChange={(e) => setPageSize(parseInt(e.target.value))}
                name="pageSize"
                value={pageSize}
              >
                {PAGE_SIZE_OPTIONS?.map(function (option, i) {
                  return (
                    <option key={i} value={option.value}>
                      {option.label}
                    </option>
                  );
                })}
              </select>
            </span>
          </div>
          <div className="column is-half has-text-right">
            {previousCursors.length > 0 && (
              <button class="button" onClick={onPreviousClicked}>
                Previous
              </button>
            )}
            {listData.hasNextPage && (
              <>
                <button class="button" onClick={onNextClicked}>
                  Next
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ActiveUsers;
